import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, DestroyRef, HostBinding, HostListener, OnDestroy, OnInit, inject, effect } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { select, Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import {
  KEYCLOAK_EVENT_SIGNAL,
  KeycloakEventType,
  KeycloakService,
  typeEventArgs,
  ReadyArgs
} from 'keycloak-angular';
import Keycloak from 'keycloak-js';
import { KeycloakProfile } from 'keycloak-js';
import { MenuItem } from 'primeng/api';
import { Subject, map, shareReplay, filter, take, combineLatest, takeUntil } from 'rxjs';
import { PrimengTranslationService } from '../../services/primeng-translation-service';
import { selectUser, selectUserState } from '../../../core/state/user/user.selectors';
import { loadUser } from '../../../core/state/user/user.actions';
import { loadCompanies } from '../../../core/state/company/company-context.actions';
import { User } from '../../../core/state/user/user.model';
import { AppointmentService } from '../../../core/services/appointment/appointment.service';
import { environment } from '../../../../environments/environment';
import { selectCompanies, selectCompanyContext, selectLoadingCompanies } from '../../../core/state/company/company-context.selectors';
import { Router } from '@angular/router';
import {
  NavigationItem,
  UserProfile,
  Language,
  EkoLanguageService,
  AppBarProfileAction,
  EkoThemeService,
  EkoBrowserTitleService,
  hideIndexLoadingScreen
} from '@ekowitryna/ui';
import packageJson from '../../../../../package.json';
import { AccountSetupService } from '../../../core/services/account-setup.service';
import { CompanyDataSetupService } from '../../../core/services/company-data-setup.service';
import { getUserRoles } from '../../../core/auth/keycloak-roles.util';
import { PackageDisplayService } from '../../../core/services/package-display.service';
import { EKOWITRYNA_SUBSCRIPTION_URL } from '../../../core/constants/subscription-url';

const MOBILE_BREAKPOINT_PX = 1024;
const SUPPORTED_LANGUAGE_CODES = ['pl', 'en', 'de', 'es', 'uk', 'fr'] as const;

@Component({
    selector: 'app-hub',
    templateUrl: './hub.component.html',
    styleUrl: './hub.component.css',
    standalone: false
})
export class HubComponent implements OnInit, OnDestroy {
  keycloakUrl = environment.keycloakUrl;
  keycloakRealm = environment.realm;
  keycloakClientId = environment.clientId;

  sidebarCollapsed = false;
  appVersion = packageJson.version;
  isMobile = false;

  navigationItems: NavigationItem[] = [];
  currentUser: UserProfile | null = null;
  authenticated = false;
  userMenuItems: MenuItem[] = [];
  profileActions: AppBarProfileAction[] = [];
  currentLanguage: string = 'pl';
  availableLanguages: Language[] = [
    { code: 'pl', name: 'Polski' },
    { code: 'en', name: 'English', flag: 'gb' },
    { code: 'de', name: 'Deutsch' },
    { code: 'es', name: 'Español' },
    { code: 'uk', name: 'Українська', flag: 'ua' },
    { code: 'fr', name: 'Français' }
  ];
  isInitializing = true;
  destroy$ = new Subject();
  user: User | null = null;

  requireFirstConfiguration = false;
  requireCompanyData = false;
  showNoCompaniesDialog = false;

  profileExternalUrl = `${environment.platformWebUrl.replace(/\/$/, '')}/profile`;
  readonly subscriptionUrl = EKOWITRYNA_SUBSCRIPTION_URL;

  loginLabel = '';
  userMenuLabel = '';
  myProfileLabel = '';
  logoutLabel = '';
  buySubscriptionLabel = '';
  themeLabel = '';
  languageLabel = '';
  toggleSidebarLabel = '';
  brandLabel = '';

  private initialLoadFinished = false;
  private setupRequired = false;
  private currentCompanies: unknown[] | null = null;
  private companiesLoading = false;
  private companiesError: string | null = null;

  private companiesDataReady = false;
  private userDataReady = false;
  private sessionBootstrapped = false;
  private bootstrapWatchdogScheduled = false;

  isHandset$ = this.breakpointObserver.observe(Breakpoints.Handset)
  .pipe(
    map(result => result.matches),
    shareReplay()
  );

  private keycloak = inject(Keycloak);
  private readonly keycloakService = inject(KeycloakService);
  private readonly accountSetupService = inject(AccountSetupService);
  private readonly companyDataSetupService = inject(CompanyDataSetupService);
  private readonly appointmentService = inject(AppointmentService);
  private readonly packageDisplay = inject(PackageDisplayService);
  private readonly darkModeService = inject(EkoThemeService);
  private readonly browserTitle = inject(EkoBrowserTitleService);
  private readonly destroyRef = inject(DestroyRef);
  private languageService = inject(EkoLanguageService);

  @HostBinding('class.sidebar-open-mobile')
  get sidebarOpenMobile(): boolean {
    return this.isMobile && !this.sidebarCollapsed;
  }

  @HostBinding('attr.data-initialized')
  get shellInitialized(): string | null {
    return this.isInitializing ? null : 'true';
  }

  get showFirstConfigurationOverlay(): boolean {
    return this.requireFirstConfiguration && this.authenticated;
  }

  get showCompanyDataOverlay(): boolean {
    return this.requireCompanyData
      && this.authenticated
      && !this.requireFirstConfiguration;
  }

  @HostListener('window:resize')
  onResize(): void {
    this.isMobile = window.innerWidth < MOBILE_BREAKPOINT_PX;
  }

  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: Event): void {
    if (this.showFirstConfigurationOverlay || this.showCompanyDataOverlay) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  constructor(
    private translate: TranslateService,
    private breakpointObserver: BreakpointObserver,
    private store: Store,
    public primengTranslationService: PrimengTranslationService,
    public router: Router
  ) {
    const keycloakSignal = inject(KEYCLOAK_EVENT_SIGNAL);

    effect(() => {
      const keycloakEvent = keycloakSignal();

      if (keycloakEvent.type === KeycloakEventType.Ready) {
        const authenticated = typeEventArgs<ReadyArgs>(keycloakEvent.args);

        if (authenticated) {
          this.bootstrapAuthenticatedSession();
        } else {
          this.authenticated = false;
          this.finishInitialization();
        }
      }

      if (keycloakEvent.type === KeycloakEventType.AuthSuccess) {
        if (this.keycloak?.authenticated && this.keycloak?.token) {
          this.bootstrapAuthenticatedSession();
        }
      }

      if (keycloakEvent.type === KeycloakEventType.AuthError || keycloakEvent.type === KeycloakEventType.AuthLogout) {
        this.authenticated = false;
        this.finishInitialization();
      }
    });

    this.accountSetupService.setupRequired$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => {
        if (state.loading) {
          return;
        }
        this.setupRequired = state.required;
        this.requireFirstConfiguration = state.required;
        if (!state.required) {
          void this.companyDataSetupService.check();
        }
        this.updateNoCompaniesDialogVisibility();
      });

    this.companyDataSetupService.companyDataRequired$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => {
        if (state.loading) {
          return;
        }
        this.requireCompanyData = state.required;
        this.updateNoCompaniesDialogVisibility();
      });

    combineLatest([
      this.store.select(selectUser),
      this.store.select(selectLoadingCompanies),
      this.store.select(selectCompanyContext)
    ])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([, loading, context]) => {
        this.currentCompanies = context.companies;
        this.companiesLoading = loading;
        this.companiesError = context.error;
        if (!loading) {
          this.initialLoadFinished = true;
          this.markCompaniesReady();
        }
        this.updateNoCompaniesDialogVisibility();
      });
  }

  private markUserReady(): void {
    if (this.userDataReady) {
      return;
    }
    this.userDataReady = true;
    this.tryFinishInitialization();
  }

  private markCompaniesReady(): void {
    if (this.companiesDataReady) {
      return;
    }
    this.companiesDataReady = true;
    this.tryFinishInitialization();
  }

  private tryFinishInitialization(): void {
    if (this.userDataReady && this.companiesDataReady) {
      this.finishInitialization();
    }
  }

  private finishInitialization(): void {
    this.isInitializing = false;
    hideIndexLoadingScreen();
  }

  private bootstrapAuthenticatedSession(): void {
    if (this.sessionBootstrapped) {
      return;
    }

    const isLoggedIn = this.keycloak?.authenticated ?? false;
    if (!isLoggedIn) {
      return;
    }

    this.sessionBootstrapped = true;
    this.authenticated = true;
    this.syncUserProfileFromToken();
    this.store.dispatch(loadUser());
    this.store.dispatch(loadCompanies());
    void this.runAccountChecks();
    void this.loadUserProfile();
  }

  private async runAccountChecks(): Promise<void> {
    await this.accountSetupService.check().catch(() => undefined);
    if (!this.accountSetupService.isSetupRequired) {
      await this.companyDataSetupService.check().catch(() => undefined);
    }
  }

  ngOnInit() {
    this.isMobile = window.innerWidth < MOBILE_BREAKPOINT_PX;
    if (this.isMobile) {
      this.sidebarCollapsed = true;
    }

    this.setupLanguageService();
    this.setupUserMenu();
    this.menuItemsInit();
    this.refreshAppBarLabels();

    this.bootstrapAuthenticatedSession();
    this.scheduleBootstrapWatchdog();

    this.store.select(selectUserState).pipe(
      filter((state) => state.user !== null || state.error !== null),
      take(1),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.markUserReady());

    this.store.select(selectCompanyContext).pipe(
      filter((state) => !state.loadingCompanies),
      take(1),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((state) => {
      this.currentCompanies = state.companies ?? [];
      this.initialLoadFinished = true;
      this.markCompaniesReady();
    });

    this.store.select(selectUser).pipe(
      takeUntil(this.destroy$)
    ).subscribe(user => {
      this.user = user;
      if (user) {
        this.authenticated = true;
        this.initNavigationMenu();
        this.setupUserProfileFromStore(user);
      }
    });

    if (this.keycloak) {
      this.keycloak.onTokenExpired = () => {
        this.keycloak.updateToken(30).then(() => {}).catch(() => {
          this.keycloak.logout({ redirectUri: window.location.origin });
        });

        this.keycloak.onAuthError = (errorData) => {
          const error = errorData?.error;
          if (error === "invalid_token" || error === "access_denied") {
            this.keycloak.logout({ redirectUri: window.location.origin });
          }
        };
      };
    }

    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.refreshAppBarLabels();
      });
  }

  private scheduleBootstrapWatchdog(): void {
    if (this.bootstrapWatchdogScheduled) {
      return;
    }

    this.bootstrapWatchdogScheduled = true;
    const maxChecks = 150;
    let checks = 0;

    const tick = () => {
      if (!this.isInitializing) {
        this.bootstrapWatchdogScheduled = false;
        return;
      }

      const isLoggedIn = this.keycloak?.authenticated ?? false;
      const keycloakInitialized = this.keycloak?.didInitialize ?? false;

      if (isLoggedIn && !this.sessionBootstrapped) {
        this.bootstrapAuthenticatedSession();
      }

      if (!isLoggedIn && keycloakInitialized) {
        this.finishInitialization();
        this.bootstrapWatchdogScheduled = false;
        return;
      }

      checks += 1;
      if (checks >= maxChecks) {
        // Last-resort safety net: avoid blank screen if init signals were missed.
        this.finishInitialization();
        this.bootstrapWatchdogScheduled = false;
        return;
      }

      setTimeout(tick, 100);
    };

    setTimeout(tick, 100);
  }

  private updateNoCompaniesDialogVisibility(): void {
    this.showNoCompaniesDialog =
      this.authenticated &&
      this.initialLoadFinished &&
      !this.setupRequired &&
      !this.requireCompanyData &&
      !this.companiesLoading &&
      !this.companiesError &&
      Array.isArray(this.currentCompanies) &&
      this.currentCompanies.length === 0;
  }

  goToCompanies(): void {
    window.location.href = `${environment.adminPanelUrl}/companies`;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  handleRefreshData() {
    this.menuItemsInit();
  }

  menuItemsInit(){
    // Removed menuItems - using only navigationItems for unified menu system
  }

  private hasConferenceAccess(): boolean {
    return this.user?.token?.resource_access?.[environment.clientId]?.roles?.includes('scope3_conferences_management') ?? false;
  }

  private hasTransportAccess(): boolean {
    return this.user?.token?.resource_access?.[environment.clientId]?.roles?.includes('scope3_transport_access') ?? false;
  }

  private hasScope3Access(): boolean {
    return this.user?.token?.resource_access?.[environment.clientId]?.roles?.includes('scope3_access') ?? false;
  }

  private hasScope3EmployeeCommuteAccess(): boolean {
    return this.user?.token?.resource_access?.[environment.clientId]?.roles?.includes('scope3_employee_commute_access') ?? false;
  }

  private hasScope3PurchasedGoodsServicesAccess(): boolean {
    return this.user?.token?.resource_access?.[environment.clientId]?.roles?.includes('scope3_purchased_goods_services_access') ?? false;
  }

  private hasSoldProductUsageAccess(): boolean {
    return this.user?.token?.resource_access?.[environment.clientId]?.roles?.includes('scope3_sold_product_usage_access') ?? false;
  }

  initNavigationMenu() {
    this.navigationItems = [
        {
          label: this.translate.instant('views.app.menu.dashboard'),
          icon: 'pi pi-home',
          routerLink: '/dashboard',
          routerLinkActiveOptions: { exact: true }
        },
        {
          label: this.translate.instant('views.app.menu.scope1.label'),
          icon: 'pi pi-chart-bar',
          expanded: true,
          items: [
            {
              label: this.translate.instant('views.app.menu.scope1.vehicle'),
              icon: 'pi pi-car',
              routerLink: '/dashboard/scope1/vehicles',
              routerLinkActiveOptions: { exact: true }
            },
            {
              label: this.translate.instant('views.app.menu.scope1.fugitiveEmission'),
              icon: 'pi pi-cloud',
              routerLink: '/dashboard/scope1/fugitiveEmission',
              routerLinkActiveOptions: { exact: true }
            },
            {
              label: this.translate.instant('views.app.menu.scope1.stationaryDevice'),
              icon: 'pi pi-cog',
              routerLink: '/dashboard/scope1/stationaryDevice',
              routerLinkActiveOptions: { exact: true }
            }
          ]
        },
        {
          label: this.translate.instant('views.app.menu.scope2.label'),
          icon: 'pi pi-chart-line',
          expanded: true,
          items: [
            {
              label: this.translate.instant('views.app.menu.scope2.heatConsumption'),
              icon: 'pi pi-sun',
              routerLink: '/dashboard/scope2/heatConsumption',
              routerLinkActiveOptions: { exact: true }
            },
            {
              label: this.translate.instant('views.app.menu.scope2.electricityConsumption'),
              icon: 'pi pi-bolt',
              routerLink: '/dashboard/scope2/electricity',
              routerLinkActiveOptions: { exact: true }
            },
            {
              label: this.translate.instant('views.app.menu.scope2.renewableEnergyConsumption'),
              icon: 'pi pi-refresh',
              routerLink: '/dashboard/scope2/renewableEnergyConsumption',
              routerLinkActiveOptions: { exact: true }
            }
          ]
        },
        {
          label: this.translate.instant('views.app.menu.scope3.label'),
          icon: 'pi pi-chart-pie',
          expanded: true,
          badge: !this.hasScope3Access() ? 'Wykup dostęp' : '',
          disabled: !this.hasScope3Access(),
          items: [
            {
              label: this.translate.instant('views.app.menu.scope3.purchasedGoodsServices'),
              icon: 'pi pi-shopping-cart',
              routerLink: '/dashboard/scope3/purchased-goods-services',
              routerLinkActiveOptions: { exact: true },
              disabled: !this.hasScope3Access() || !this.hasScope3PurchasedGoodsServicesAccess(),
              tooltip: !this.hasScope3Access() || this.hasScope3PurchasedGoodsServicesAccess() ? '' : this.translate.instant('views.app.menu.notAvailable'),
              visible: this.hasScope3Access()
            },
            {
              label: this.translate.instant('views.app.menu.scope3.soldProductUsage'),
              icon: 'pi pi-box',
              routerLink: '/dashboard/scope3/sold-product-usage',
              disabled: !this.hasScope3Access() || !this.hasSoldProductUsageAccess(),
              tooltip: !this.hasScope3Access() || this.hasSoldProductUsageAccess() ? '' : this.translate.instant('views.app.menu.notAvailable'),
              visible: this.hasScope3Access(),
              routerLinkActiveOptions: { exact: true }
            },
            {
              label: this.translate.instant('views.app.menu.scope3.transport_upstream'),
              icon: 'pi pi-truck',
              routerLink: '/dashboard/scope3/transport/upstream',
              routerLinkActiveOptions: { exact: true },
              disabled: !this.hasScope3Access() || !this.hasTransportAccess(),
              tooltip: !this.hasScope3Access() || this.hasTransportAccess() ? '' : this.translate.instant('views.app.menu.no_transport_access'),
              visible: this.hasScope3Access()
            },
            {
              label: this.translate.instant('views.app.menu.scope3.transport_downstream'),
              icon: 'pi pi-send',
              routerLink: '/dashboard/scope3/transport/downstream',
              routerLinkActiveOptions: { exact: true },
              disabled: !this.hasScope3Access() || !this.hasTransportAccess(),
              tooltip: !this.hasScope3Access() || this.hasTransportAccess() ? '' : this.translate.instant('views.app.menu.no_transport_access'),
              visible: this.hasScope3Access()
            },
            {
              label: this.translate.instant('views.app.menu.scope3.employee_commute'),
              icon: 'pi pi-users',
              routerLink: '/dashboard/scope3/employee-commute',
              routerLinkActiveOptions: { exact: true },
              disabled: !this.hasScope3Access() || !this.hasScope3EmployeeCommuteAccess(),
              tooltip: !this.hasScope3Access() || this.hasScope3EmployeeCommuteAccess() ? '' : this.translate.instant('views.app.menu.notAvailable'),
              visible: this.hasScope3Access()
            },
            {
              label: this.translate.instant('views.app.menu.scope3.conferences'),
              icon: 'pi pi-users',
              routerLink: '/dashboard/scope3/conferences',
              routerLinkActiveOptions: { exact: true },
              disabled: !this.hasScope3Access() || !this.hasConferenceAccess(),
              tooltip: this.hasConferenceAccess() ? '' : this.translate.instant('views.app.menu.no_conference_access'),
              visible: this.hasConferenceAccess()
            },
            {
              label: this.translate.instant('views.app.menu.scope3.remoteWork'),
              icon: 'pi pi-home',
              routerLink: '/dashboard/scope3/remoteWork',
              disabled: true,
              routerLinkActiveOptions: { exact: true }
            },
            {
              label: this.translate.instant('views.app.menu.scope3.businessTravel'),
              icon: 'pi pi-briefcase',
              routerLink: '/dashboard/scope3/businessTravel',
              disabled: true,
              routerLinkActiveOptions: { exact: true }
            },
            {
              label: this.translate.instant('views.app.menu.scope3.capitalGoods'),
              icon: 'pi pi-building',
              routerLink: '/dashboard/scope3/capitalGoods',
              disabled: true,
              routerLinkActiveOptions: { exact: true }
            }
          ]
        }
      ];
      this.browserTitle.setNavigationItems(this.navigationItems);
  }

  setupLanguageService() {
    this.currentLanguage = this.languageService.currentLanguage;

    this.languageService.currentLanguage$
      .pipe(takeUntil(this.destroy$))
      .subscribe((lang: string) => {
        this.currentLanguage = lang;
      });

    this.translate.onLangChange
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.primengTranslationService.setTranslations();
        this.initNavigationMenu();
        this.setupUserMenu();
      });
  }

  private refreshAppBarLabels(): void {
    const keys = [
      'app.brand',
      'headerProfile.login',
      'headerProfile.consultations',
      'headerProfile.myProfile',
      'headerProfile.userMenu',
      'headerProfile.logout',
      'headerProfile.buySubscription',
      'settings.theme',
      'common.language',
      'a11y.toggleSidebar',
    ];

    this.translate.get(keys).subscribe(translations => {
      this.brandLabel = translations['app.brand'] ?? 'CO₂';
      this.loginLabel = translations['headerProfile.login'] ?? '';
      this.userMenuLabel = translations['headerProfile.userMenu'] ?? '';
      this.myProfileLabel = translations['headerProfile.myProfile'] ?? '';
      this.logoutLabel = translations['headerProfile.logout'] ?? '';
      this.buySubscriptionLabel = translations['headerProfile.buySubscription'] ?? '';
      this.themeLabel = translations['settings.theme'] ?? '';
      this.languageLabel = translations['common.language'] ?? '';
      this.toggleSidebarLabel = translations['a11y.toggleSidebar'] ?? '';

      this.profileActions = [
        { label: translations['headerProfile.consultations'] ?? '', action: 'consultations', icon: 'pi pi-calendar' }
      ];
    });
  }

  async loadUserProfile(): Promise<void> {
    const isLoggedIn =
      this.keycloak?.authenticated ??
      this.keycloakService.isLoggedIn();

    if (!isLoggedIn) {
      this.authenticated = false;
      this.currentUser = null;
      this.darkModeService.initFromStorageOrSystem();
      return;
    }

    this.authenticated = true;
    await this.darkModeService.syncFromProfile();
    await this.languageService.syncFromProfile([...SUPPORTED_LANGUAGE_CODES]);

    try {
      const profile: KeycloakProfile = await this.keycloakService.loadUserProfile();
      const roles = getUserRoles(this.keycloak);

      this.currentUser = {
        name: `${profile.firstName || ''} ${profile.lastName || ''}`.trim() || profile.username || '',
        email: profile.email || profile.username || '',
        avatar: `https://ui-avatars.com/api/?size=128&background=random&name=${profile.firstName}+${profile.lastName}`,
        role: roles && roles.length > 0 ? roles[0] : undefined
      };
      this.applySubscriptionBadge();
    } catch {
      this.syncUserProfileFromToken();
    }

    await this.runAccountChecks();
  }

  private syncUserProfileFromToken(): void {
    const token = this.keycloak?.tokenParsed as Record<string, string | undefined> | undefined;
    if (!token) {
      return;
    }

    const name =
      `${token['given_name'] ?? ''} ${token['family_name'] ?? ''}`.trim() ||
      token['name'] ||
      token['preferred_username'] ||
      'Użytkownik';

    this.currentUser = {
      name,
      email: token['email'] || token['preferred_username'] || '',
      avatar: `https://ui-avatars.com/api/?size=128&background=random&name=${encodeURIComponent(name)}`,
    };
    this.applySubscriptionBadge();
  }

  private applySubscriptionBadge(): void {
    if (!this.currentUser) {
      return;
    }

    this.currentUser = { ...this.currentUser, subscriptionBadge: 'Free' };
    this.packageDisplay.getPackageDisplayName$().pipe(take(1)).subscribe({
      next: (name) => {
        if (!this.currentUser) {
          return;
        }
        const badge = name?.trim() || 'Free';
        if (this.currentUser.subscriptionBadge === badge) {
          return;
        }
        this.currentUser = { ...this.currentUser, subscriptionBadge: badge };
      },
      error: () => {
        if (!this.currentUser) {
          return;
        }
        this.currentUser = { ...this.currentUser, subscriptionBadge: 'Free' };
      },
    });
  }

  setupUserProfileFromStore(user: User) {
    this.authenticated = true;

    if (this.currentUser?.name) {
      return;
    }

    try {
      const firstName = user.token?.given_name || user.profile?.firstName || '';
      const lastName = user.token?.family_name || user.profile?.lastName || '';
      const displayName = firstName && lastName ? `${firstName} ${lastName}` :
                         user.token?.name || user.token?.preferred_username || user.profile?.username || 'Użytkownik';

      this.currentUser = {
        name: displayName,
        email: user.token?.preferred_username || user.token?.email || user.profile?.email || user.profile?.username || '',
        role: user.roles?.join(', ') || ''
      };
      this.applySubscriptionBadge();
    } catch {
      this.currentUser = {
        name: user.profile?.username || 'Użytkownik',
        email: user.profile?.email || user.profile?.username || ''
      };
    }
  }

  setupUserMenu() {
    this.userMenuItems = [];
  }

  onLanguageChange(language: string) {
    this.languageService.setLanguage(language);
  }

  onMenuClick(item: NavigationItem) {
    if (item.routerLink) {
      const route = Array.isArray(item.routerLink) ? item.routerLink.join('/') : item.routerLink;

      this.router.navigateByUrl(route).then((success) => {
        if (success) {
          setTimeout(() => {
            this.scrollToTop();
          }, 200);
        }
      });
    }
    this.closeSidebarMobile();
  }

  onMenuClosed() {
    this.closeSidebarMobile();
  }

  onProfileAction(action: string) {
    switch (action) {
      case 'logout':
        this.logout();
        break;
      case 'consultations':
        this.openConsultations();
        break;
    }
  }

  /**
   * Opens the personalised consultation booking page. The link is resolved from
   * the legacy platform API (per-user appointment services), so we open the tab
   * synchronously within the click gesture to dodge popup blockers, then point
   * it at the resolved URL. Falls back to the static page on any error.
   */
  private openConsultations(): void {
    const tab = window.open('', '_blank');
    this.appointmentService.getConsultationLink().subscribe({
      next: (link) => this.navigateConsultationTab(tab, link),
      error: () => this.navigateConsultationTab(tab, AppointmentService.FALLBACK_URL),
    });
  }

  private navigateConsultationTab(tab: Window | null, url: string): void {
    if (tab) {
      tab.location.href = url;
    } else {
      // Popup was blocked at open time — retry once as a best effort.
      window.open(url, '_blank');
    }
  }

  onProfileMenuClick(menuItem: MenuItem) {
    if (menuItem.command) {
      menuItem.command({});
    }
  }

  scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  logout() {
    this.keycloak.logout({ redirectUri: window.location.origin });
  }

  onSidebarToggle() {
    this.sidebarCollapsed = !this.sidebarCollapsed;
  }

  closeSidebarMobile(): void {
    if (this.isMobile) {
      this.sidebarCollapsed = true;
    }
  }

  get currentRoute(): string {
    return this.router.url;
  }
}
